import { Badge, Button, Loader, Progress, SegmentedControl } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BudgetReportPrintSheet } from 'src/Components/Expenses/BudgetReportPrintSheet';
import { PrintSection } from 'src/Components/Expenses/BudgetReportPrintSheet/BudgetReportPrintSheet.types';
import useBudgetTracking from 'src/Hooks/Queries/useBudgetTracking';
import useBudgetYear from 'src/Hooks/Queries/useBudgetYear';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import useMemberAnnualForecastPosition from 'src/Hooks/Queries/useMemberAnnualForecastPosition';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { trackBudgetReportPrinted, trackBudgetTrackingViewed } from 'src/Services/Mixpanel/events';
import { formatEuros, formatPercent } from 'src/Utils/budgetWizardMath';
import { getCategoryLabel } from 'src/Utils/expenseCategories';

import styles from './BudgetTracking.module.scss';
import { Props, TrackingViewRow, VarianceMode } from './BudgetTracking.types';

const SECTION_ORDER = ['FIXED_COSTS', 'DAILY_LIFE', 'PROJECTS', 'INCOME'];

/** « Conforme » threshold: one percent of what was planned. */
const CONFORMITY_RATIO = 0.01;

/** Same format as the currency amounts in Mon solde: xx xxx,xx € (with cents). */
const formatAmountWithCents = (value: number): string =>
  value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const BudgetTracking = ({ year }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: asset } = useFetchAssetDetails();
  const { data: user } = useFetchUser();
  const { data: tracking, isLoading: isTrackingLoading } = useBudgetTracking(year);
  const { data: budgetYear } = useBudgetYear(year);
  const { data: forecast } = useMemberAnnualForecastPosition(assetId, user?.id, year);

  const [varianceMode, setVarianceMode] = useState<VarianceMode>('euro');

  useEffect(() => {
    if (!assetId) return;
    trackBudgetTrackingViewed({ asset_id: assetId, year });
  }, [assetId, year]);

  /**
   * The daily-life envelope carries no reconciliation category, so the API cannot match it: its
   * real spending comes back under « non prévu ». Re-attaching that section total to the
   * envelope is what makes the comparison the brief asks for (§3, rule 5) readable, and the
   * same rows are then kept out of the « non prévu » block so nothing is counted twice.
   */
  const dailyLifeActual = useMemo(
    () =>
      (tracking?.unplanned ?? [])
        .filter(row => row.section === 'DAILY_LIFE' && !row.isIncome)
        .reduce((sum, row) => sum + row.actualAmount, 0),
    [tracking]
  );

  const sections = useMemo(() => {
    if (!tracking) return [];

    const bySection = new Map<string, TrackingViewRow[]>();
    for (const row of tracking.rows) {
      const isEnvelope = row.section === 'DAILY_LIFE' && row.category == null;
      // A reconciled line with no real operation behind it comes back at zero. Reading that as
      // « 2 400 € sous le budget » would claim a saving that has not happened: nothing has been
      // recorded yet, and the row says exactly that.
      const observedAmount = isEnvelope ? dailyLifeActual : (row.actualAmount ?? 0);
      const hasBeenObserved = isEnvelope
        ? dailyLifeActual > 0
        : row.isReconciled && row.actualExpenseCount > 0;

      const viewRow: TrackingViewRow = {
        key: row.key,
        label:
          row.subCategoryEntryLabel ??
          (row.category
            ? t(`expense.type.${row.category}`)
            : getCategoryLabel(row.mainCategory, row.subCategory)),
        section: row.section,
        plannedAmount: row.plannedAmount,
        actualAmount: hasBeenObserved ? observedAmount : null,
        isEnvelope,
      };
      const existing = bySection.get(row.section) ?? [];
      existing.push(viewRow);
      bySection.set(row.section, existing);
    }

    return SECTION_ORDER.filter(section => bySection.has(section)).map(section => ({
      section,
      label: t(`budgetV2.section.${section}`, section),
      rows: bySection.get(section) ?? [],
    }));
  }, [tracking, dailyLifeActual, t]);

  const remainingUnplanned = useMemo(
    () =>
      (tracking?.unplanned ?? []).filter(row => !(row.section === 'DAILY_LIFE' && !row.isIncome)),
    [tracking]
  );

  if (isTrackingLoading || !tracking) {
    return (
      <div className={styles.loading}>
        <Loader size="sm" />
      </div>
    );
  }

  /** The « Écart » cell, in euros, in percent, or both when the report is printed. */
  const varianceText = (row: TrackingViewRow, mode: VarianceMode): string => {
    if (row.actualAmount == null) {
      return t('budgetV2.tracking.notObserved', 'Pas encore constaté');
    }

    const difference = row.actualAmount - row.plannedAmount;
    // A plan of zero has no percentage to speak of: euros only, in every mode.
    const percent = row.plannedAmount > 0 ? Math.abs(difference / row.plannedAmount) * 100 : null;

    const express = (value: number): string => {
      if (mode === 'euro' || percent === null) return formatEuros(value);
      if (mode === 'percent') return formatPercent((value / row.plannedAmount) * 100);
      return `${formatEuros(value)} (${formatPercent((value / row.plannedAmount) * 100)})`;
    };

    if (row.isEnvelope) {
      const remaining = row.plannedAmount - row.actualAmount;
      return remaining >= 0
        ? t('budgetV2.tracking.remaining', '{{value}} restants', { value: express(remaining) })
        : t('budgetV2.tracking.over', '+{{value}} de plus que prévu', {
            value: express(Math.abs(remaining)),
          });
    }

    if (Math.abs(difference) <= row.plannedAmount * CONFORMITY_RATIO) {
      return t('budgetV2.tracking.conform', 'Conforme');
    }

    return difference > 0
      ? t('budgetV2.tracking.over', '+{{value}} de plus que prévu', {
          value: express(Math.abs(difference)),
        })
      : t('budgetV2.tracking.under', '{{value}} sous le budget', {
          value: express(Math.abs(difference)),
        });
  };

  const varianceColor = (row: TrackingViewRow): string => {
    if (row.actualAmount == null) return 'gray';
    const difference = row.actualAmount - row.plannedAmount;
    if (row.isEnvelope) return row.plannedAmount - row.actualAmount >= 0 ? 'green' : 'orange';
    if (Math.abs(difference) <= row.plannedAmount * CONFORMITY_RATIO) return 'green';
    return difference > 0 ? 'orange' : 'green';
  };

  const calculatedCap = budgetYear?.calculatedCap ?? 0;
  // Everything spent this year, budgeted or not — the same figure the hub shows. Reading
  // actualTotal here meant the gauge announced « Réalisé 0 € » on a house that had spent
  // 1 518 €, because none of that spending matched a budget line. The Prévu / Réel / Écart
  // table below still works line by line, where actualTotal is the right number.
  const realizedTotal = tracking.spentTotal;
  const consumedPercent =
    calculatedCap > 0 ? Math.min(100, Math.round((realizedTotal / calculatedCap) * 100)) : 0;

  const capText = t(
    'budgetV2.tracking.capLine',
    'Plafond calculé {{year}} : {{cap}} · réalisé {{actual}} ({{percent}} %)',
    {
      year,
      cap: formatEuros(calculatedCap),
      actual: formatEuros(realizedTotal),
      percent: consumedPercent,
    }
  );

  const unplannedText = remainingUnplanned.length
    ? t(
        'budgetV2.tracking.unplannedText',
        "{{list}}. Ces montants réels n'étaient rattachés à aucune ligne du budget — à prévoir peut-être l'an prochain.",
        {
          list: remainingUnplanned
            .map(row => `${t(`expense.type.${row.category}`)} : ${formatEuros(row.actualAmount)}`)
            .join(' · '),
        }
      )
    : null;

  const printSections: PrintSection[] = sections.map(section => ({
    key: section.section,
    label: section.label,
    rows: section.rows.map(row => ({
      key: row.key,
      label: row.label,
      plannedText: formatEuros(row.plannedAmount),
      actualText: row.actualAmount == null ? '—' : formatEuros(row.actualAmount),
      // Printed reports always carry both units: the reader has no toggle on paper.
      varianceText: varianceText(row, 'both'),
    })),
  }));

  const print = () => {
    trackBudgetReportPrinted({ year });
    document.body.classList.add('sh-print-report');
    try {
      window.print();
    } finally {
      document.body.classList.remove('sh-print-report');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <div className={styles.lockBanner}>
          🔒{' '}
          <span>
            {t(
              'budgetV2.tracking.lockBanner',
              'Ce budget ne modifie jamais vos opérations. Il sert uniquement à comparer le prévu et le réalisé.'
            )}
          </span>
        </div>
        <Button variant="outline" size="md" leftSection={<IconPrinter size={18} />} onClick={print}>
          {t('budgetV2.tracking.print', 'Imprimer le bilan')}
        </Button>
      </div>

      {calculatedCap > 0 ? (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>
            {t('budgetV2.tracking.pathToCap', 'Chemin vers le plafond {{year}}', { year })}
          </h3>
          <Progress value={consumedPercent} size="lg" radius="xl" color="green" />
          <div className={styles.pathLabels}>
            <span>
              {t('budgetV2.tracking.realized', 'Réalisé {{amount}}', {
                amount: formatEuros(realizedTotal),
              })}
            </span>
            <span>
              {t('budgetV2.tracking.calculatedCap', 'Plafond calculé {{amount}}', {
                amount: formatEuros(calculatedCap),
              })}
            </span>
          </div>
          {forecast ? (
            <div className={styles.myShare}>
              {forecast.remainingToProvision > 0 ? (
                t(
                  'budgetV2.tracking.myShare.toVerse',
                  'Votre part restante {{year}} : {{amount}} à verser',
                  {
                    year,
                    amount: formatAmountWithCents(forecast.remainingToProvision),
                  }
                )
              ) : forecast.remainingToProvision === 0 ? (
                <span className={styles.myShareSettled}>
                  {t('budgetV2.tracking.myShare.settled', 'Votre part {{year}} est versée', {
                    year,
                  })}
                </span>
              ) : (
                <span className={styles.myShareExcess}>
                  {t(
                    'budgetV2.tracking.myShare.excess',
                    'Vous avez versé {{amount}} de plus que votre part {{year}}',
                    {
                      year,
                      amount: formatAmountWithCents(Math.abs(forecast.remainingToProvision)),
                    }
                  )}
                </span>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>
            {t('budgetV2.tracking.title', 'Prévu / Réel / Écart')}
          </h3>
          <SegmentedControl
            size="md"
            value={varianceMode}
            onChange={value => setVarianceMode(value as VarianceMode)}
            data={[
              { value: 'euro', label: '€' },
              { value: 'percent', label: '%' },
            ]}
            aria-label={t('budgetV2.tracking.varianceUnit', "Unité de l'écart")}
          />
        </div>

        {sections.length === 0 ? (
          <p className={styles.empty}>
            {t(
              'budgetV2.tracking.empty',
              "Aucune ligne budgétaire pour {{year}}. Préparez d'abord votre budget.",
              { year }
            )}
          </p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('budgetV2.tracking.line', 'Ligne')}</th>
                  <th>{t('budgetV2.tracking.planned', 'Prévu')}</th>
                  <th>{t('budgetV2.tracking.actual', 'Réel')}</th>
                  <th>{t('budgetV2.tracking.variance', 'Écart')}</th>
                </tr>
              </thead>
              <tbody>
                {sections.map(section => (
                  <Fragment key={section.section}>
                    <tr className={styles.sectionRow}>
                      <td colSpan={4}>{section.label}</td>
                    </tr>
                    {section.rows.map(row => (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        <td>{formatEuros(row.plannedAmount)}</td>
                        <td>{row.actualAmount == null ? '—' : formatEuros(row.actualAmount)}</td>
                        <td>
                          <Badge color={varianceColor(row)} variant="light">
                            {varianceText(row, varianceMode)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {unplannedText ? (
        <div className={styles.unplanned}>
          <h3 className={styles.unplannedTitle}>
            {t('budgetV2.tracking.unplannedTitle', 'Constaté mais non prévu au budget')}
          </h3>
          <p className={styles.unplannedText}>{unplannedText}</p>
        </div>
      ) : null}

      <BudgetReportPrintSheet
        assetName={asset?.name ?? ''}
        year={year}
        editedOn={dayjs().format('DD/MM/YYYY')}
        sections={printSections}
        capText={capText}
        unplannedText={unplannedText}
      />
    </div>
  );
};

export default BudgetTracking;
